import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout,
} from "@stripe/react-stripe-js";
import { useAuth } from "./context/AuthContext";
import api from "./services/api";

// Replace with your actual Stripe publishable key
const stripePromise = loadStripe(
  "pk_test_51R1gSgE1A6TTIkJhVmuNqSmvdzOxSQrcncLhjTbmFtUp69gw4HPKkdqgNDth0hcvk0VaWPu77UiryAD8batD5PZB007Tng3Ukq"
);

export default function CheckoutPage() {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { refreshUserData } = useAuth();

  useEffect(() => {
    // Get client secret from URL query parameter
    const queryParams = new URLSearchParams(location.search);
    const secret = queryParams.get("client_secret");

    if (secret) {
      setClientSecret(secret);
      setLoading(false);
    } else {
      setError("Missing payment information. Please try again.");
      setLoading(false);
    }
  }, [location]);

  // This will be called when the user is returned after payment
  useEffect(() => {
    const handlePaymentSuccess = async () => {
      const queryParams = new URLSearchParams(location.search);
      const sessionId = queryParams.get("session_id");

      if (sessionId && location.pathname.includes("/payment/success")) {
        try {
          // Call backend to check session status using api service
          const response = await api.get(
            `/api/payment/session-status/${sessionId}`
          );

          if (response.data.status === "complete") {
            // Payment successful, refresh user data to get updated credits
            await refreshUserData();
            // Show success message
            alert("Payment successful! Your credits have been updated.");
            // Redirect to dashboard
            navigate("/dashboard");
          }
        } catch (error) {
          console.error("Error checking payment status:", error);
        }
      }
    };

    handlePaymentSuccess();
  }, [location, navigate, refreshUserData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen px-4">
        <div className="bg-red-50 p-6 rounded-lg max-w-md w-full">
          <h2 className="text-xl font-semibold text-red-700 mb-3">
            Something went wrong
          </h2>
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => navigate("/pricing")}
            className="w-full py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition"
          >
            Back to Pricing
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto p-6 min-h-screen flex flex-col">
      <h1 className="text-2xl font-bold mb-6 text-center">
        Complete Your Purchase
      </h1>

      {clientSecret && (
        <div className="bg-white rounded-lg shadow-md p-4 flex-grow">
          <EmbeddedCheckoutProvider
            stripe={stripePromise}
            options={{ clientSecret }}
          >
            <EmbeddedCheckout />
          </EmbeddedCheckoutProvider>
        </div>
      )}
    </div>
  );
}
